/**
 * Callout — Dummies-style icon-callout. Four locked variants (Tip / Watch Out
 * / Remember / Key Insight) per /shared/design-tokens.v2.json `callout.*`.
 *
 * Usage:
 *   <Callout icon={<TipIcon />} label="Tip">body content...</Callout>
 *
 * The four type-specific variants are exposed as named exports below, so
 * surfaces can write the most ergonomic form:
 *   <Callout.Tip>...</Callout.Tip>
 *
 * Section 5 Tension Alignment guardrail (Operating Manual) and Section 8
 * Power Asymmetry forced render (Relational Blueprint) both use the
 * Callout.WatchOut and Callout.KeyInsight variants per /policy/brand-discipline.md.
 */

import TipIcon from "./icons/TipIcon.jsx";
import WatchOutIcon from "./icons/WatchOutIcon.jsx";
import RememberIcon from "./icons/RememberIcon.jsx";
import KeyIcon from "./icons/KeyIcon.jsx";

function Callout({ icon, label, labelColor, children }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "56px 1fr",
        gap: 16,
        margin: "20px 0",
        alignItems: "start",
      }}
      data-component="Callout"
    >
      <div style={{ paddingTop: 2 }}>{icon}</div>
      <div>
        <div
          style={{
            fontFamily: "var(--ppr-typography-font-family-display)",
            fontSize: "var(--ppr-typography-font-size-sm)",
            letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
            textTransform: "uppercase",
            color: labelColor || "var(--ppr-color-text-primary)",
            marginBottom: 4,
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontFamily: "var(--ppr-typography-font-family-body)",
            fontSize: "var(--ppr-typography-font-size-base)",
            lineHeight: "var(--ppr-typography-line-height-relaxed)",
            color: "var(--ppr-color-text-primary)",
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

// Type-specific variants — labelColor pulled from the locked callout tokens.
function CalloutTip({ children }) {
  return (
    <Callout
      icon={<TipIcon />}
      label="Tip"
      labelColor="var(--ppr-callout-tip-label-color)"
    >
      {children}
    </Callout>
  );
}
CalloutTip.displayName = "Callout.Tip";

function CalloutWatchOut({ children }) {
  return (
    <Callout
      icon={<WatchOutIcon />}
      label="Watch out"
      labelColor="var(--ppr-callout-watch-out-label-color)"
    >
      {children}
    </Callout>
  );
}
CalloutWatchOut.displayName = "Callout.WatchOut";

function CalloutRemember({ children }) {
  return (
    <Callout
      icon={<RememberIcon />}
      label="Remember"
      labelColor="var(--ppr-callout-remember-label-color)"
    >
      {children}
    </Callout>
  );
}
CalloutRemember.displayName = "Callout.Remember";

function CalloutKeyInsight({ children }) {
  return (
    <Callout
      icon={<KeyIcon />}
      label="Key insight"
      labelColor="var(--ppr-callout-key-insight-label-color)"
    >
      {children}
    </Callout>
  );
}
CalloutKeyInsight.displayName = "Callout.KeyInsight";

Callout.Tip = CalloutTip;
Callout.WatchOut = CalloutWatchOut;
Callout.Remember = CalloutRemember;
Callout.KeyInsight = CalloutKeyInsight;

export default Callout;
